/**
 * SoaTab
 *
 * Main Statement of Applicability tab for the audit detail page.
 * Fetches SoA data, groups entries by annex, and renders controls with
 * applicability toggles, justification fields, and confirm/amend actions.
 */

import { useMemo, useState } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { motion } from "framer-motion";
import { Loader2, Info, CheckCircle, ArrowRightLeft } from "lucide-react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";
import type { AuditId, Id } from "@/packages/types";
import { SoaControlRow, type SoaEntry } from "./SoaControlRow";
import { SoaExportButton } from "./SoaExportButton";

// ─── Props ───────��──────────────────────────────────────────────────────────

interface SoaTabProps {
  auditId: AuditId;
}

// ─── Helpers ─────���──────────────────────────────��───────────────────────────

/**
 * Group entries by annex letter prefix (e.g. "A.5.1" → "A", "B.8.2" → "B").
 * Entries without a recognisable annex prefix go under "main".
 */
function groupByAnnexPrefix(entries: SoaEntry[]): Map<string, SoaEntry[]> {
  const groups = new Map<string, SoaEntry[]>();
  for (const entry of entries) {
    const match = entry.clauseNumber.match(/^([A-Z])\./);
    const key = match ? match[1] : "main";
    const existing = groups.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return groups;
}

// ─── Component ─────────────────��────────────────────────────────────────────

export function SoaTab({ auditId }: SoaTabProps) {
  const { t } = useTranslation(["soa", "common"]);

  // Fetch SoA data
  const soaData = useConvexQuery(api.soa_queries.getSoaByAudit, { auditId });

  // Carry-forward candidate
  const { data: carryForward } = useWorkspaceQuery(
    api.soa_queries.getCarryForwardCandidate,
    soaData
      ? {
          standardId: soaData.standardId,
          currentAuditId: auditId,
        }
      : "skip",
  );

  // Mutations
  const updateApplicability = useApiMutation(
    api.soa_mutations.updateApplicability,
    {
      name: "update-soa-applicability",
    },
  );

  const confirmSoa = useApiMutation(api.soa_mutations.confirmSoa, {
    name: "confirm-soa",
  });

  const reopenSoa = useApiMutation(api.soa_mutations.reopenSoa, {
    name: "reopen-soa",
  });

  const carryForwardMutation = useApiMutation(
    api.soa_mutations.carryForwardSoa,
    {
      name: "carry-forward-soa",
    },
  );

  // Carry-forward dismissed state
  const [carryForwardDismissed, setCarryForwardDismissed] = useState(false);

  // Compute grouped entries
  const { normativeEntries, groups, stats } = useMemo(() => {
    if (!soaData?.entries) {
      return {
        normativeEntries: [],
        groups: new Map<string, SoaEntry[]>(),
        stats: { applicable: 0, excluded: 0, total: 0 },
      };
    }

    const entries = soaData.entries as SoaEntry[];
    const piiRole = soaData.piiRole;

    // Filter by piiRole: controllers don't see Annex B controls, processors
    // don't see Annex A controls. Derive annex letter from clauseNumber prefix.
    const filtered = entries.filter((e) => {
      if (!piiRole) return true;
      const match = e.clauseNumber.match(/^([A-Z])\./);
      if (!match) return true;
      const annexLetter = match[1];
      if (piiRole === "controller") return annexLetter !== "B";
      if (piiRole === "processor") return annexLetter !== "A";
      return true; // "both" sees everything
    });

    // All SoA entries are annex controls (prefill only creates entries for
    // normative annex clauses), so they are all normative.
    const applicable = filtered.filter((e) => e.applicable).length;
    const excluded = filtered.filter((e) => !e.applicable).length;

    return {
      normativeEntries: filtered,
      groups: groupByAnnexPrefix(filtered),
      stats: { applicable, excluded, total: filtered.length },
    };
  }, [soaData]);

  // Handlers
  const handleUpdate = (
    entryId: Id<"statementOfApplicability">,
    applicable: boolean,
    justification?: string,
  ) => {
    updateApplicability.mutate({
      entryId,
      applicable,
      justification,
    });
  };

  const handleConfirm = () => {
    confirmSoa.mutate({ auditId });
  };

  const handleReopen = () => {
    reopenSoa.mutate({ auditId });
  };

  const handleCarryForward = () => {
    if (!carryForward) return;
    carryForwardMutation.mutate({
      sourceAuditId: carryForward.auditId,
      targetAuditId: auditId,
    });
    setCarryForwardDismissed(true);
  };

  const soaStatus = soaData?.soaStatus;
  const isConfirmed = soaStatus === "confirmed";

  // ─── Loading / empty states ─────────────────────────────────────────────

  if (soaData === undefined) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-secondary" />
      </div>
    );
  }

  if (
    soaData !== null &&
    (soaData.soaStatus === "pending" || soaData.soaStatus === "generating") &&
    normativeEntries.length === 0
  ) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2
            size={48}
            className="text-muted animate-spin"
            strokeWidth={1}
          />
          <p className="text-sm text-muted">{t("bannerGenerating")}</p>
        </div>
      </div>
    );
  }

  if (soaData === null || normativeEntries.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Info size={48} className="text-muted" strokeWidth={1} />
          <p className="text-sm text-muted">{t("noEntries")}</p>
        </div>
      </div>
    );
  }

  // ─── Render ──────���──────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Status banner with actions */}
      <div className="shrink-0 mb-4">
        {soaStatus === "generating" && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30 px-4 py-2.5 text-[13px] text-blue-700 dark:text-blue-300">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("soa:bannerGenerating")}
          </div>
        )}

        {soaStatus === "ready" && (
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-2.5">
            <div className="flex items-center gap-2 text-[13px] text-amber-700 dark:text-amber-300">
              <Info size={16} />
              {t("soa:banner")}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[12px] text-secondary">
                <span className="font-semibold text-primary">
                  {stats.applicable}
                </span>{" "}
                {t("soa:controls.applicable")}
                <span className="text-border mx-1.5">|</span>
                <span className="font-semibold text-primary">
                  {stats.excluded}
                </span>{" "}
                {t("soa:controls.excluded")}
              </span>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                icon={CheckCircle}
              >
                {t("soa:actions.confirm")}
              </Button>
            </div>
          </div>
        )}

        {soaStatus === "confirmed" && (
          <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/30 px-4 py-2.5">
            <div className="flex items-center gap-2 text-[13px] text-emerald-700 dark:text-emerald-300">
              <CheckCircle size={16} />
              {t("soa:bannerConfirmed")}
            </div>
            <div className="flex items-center gap-2">
              {soaData && (
                <SoaExportButton
                  auditId={auditId}
                  standardId={soaData.standardId}
                />
              )}
              <Button variant="secondary" size="sm" onClick={handleReopen}>
                {t("soa:actions.reopen")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Carry-forward prompt */}
      {carryForward && !carryForwardDismissed && soaStatus === "ready" && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="shrink-0 mb-4 rounded-lg border border-indigo-200 bg-indigo-50/50 dark:border-indigo-800 dark:bg-indigo-950/30 px-4 py-3"
        >
          <div className="flex items-start gap-3">
            <ArrowRightLeft
              size={18}
              className="text-indigo-500 mt-0.5 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-indigo-700 dark:text-indigo-300">
                {t("soa:carryForward.title")}
              </p>
              <p className="text-[12px] text-indigo-600/80 dark:text-indigo-400/80 mt-0.5">
                {t("soa:carryForward.description", {
                  auditName: carryForward.auditName,
                })}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCarryForward}
                >
                  {t("soa:carryForward.accept")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCarryForwardDismissed(true)}
                >
                  {t("soa:carryForward.decline")}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Scrollable control list */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pb-4">
        {/* Grouped normative entries */}
        {Array.from(groups.entries()).map(([annexKey, entries]) => (
          <div key={annexKey}>
            {annexKey !== "main" && (
              <h3 className="text-[12px] font-semibold text-secondary uppercase tracking-wider mb-2 px-1">
                {annexKey}
              </h3>
            )}
            <div className="space-y-2">
              {entries.map((entry) => (
                <SoaControlRow
                  key={entry._id}
                  entry={entry}
                  onUpdate={handleUpdate}
                  disabled={isConfirmed}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
